import { ContentWrapper, LeftContainer, NotifyModalBody, RightContainer } from './EditEvent.styles';
import { IdParamsType, StateProps } from 'utils/types';
import { defaultValues, getFormDetails, handleInitialValues } from './utils';
import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { EditEventFormProps } from './types';
import { EventTitle } from './components/EventTitle';
import { Footer } from 'features/common/components/Footer';
import { Form } from 'features/common/components/Form';
import { LeftForm } from './components/LeftForm';
import { MainLayout } from 'layouts/MainLayout';
import { Modal } from 'features/common/components/Modal/Modal';
import { OUTLINED, QUATERNARY, TERTIARY } from 'features/common/components/Button/constants';
import { RightForm } from './components/RightForm';
import { Button } from 'features/common/components/Button/Button';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { convertDateWithTimezoneToUTC } from 'utils/utils';
import { useNotifyAttendeesOnDateChange } from 'features/common/hooks/useNotifyAttendeesOnDateChange';
// The email preview is intrinsically light (it renders the real email inside its own iframe), so the
// green composite is reused here rather than duplicating the fetch + handlebars + iframe in the red zone.
import {
	DateChangeEmailPreview,
	buildDateChangePreview,
} from 'features/common/components/Punchup/composites/dialogs/PunchupNotifyDateChangeDialog';

export const EditEventForm = ({ event, editEvent }: EditEventFormProps) => {
	const { t } = useTranslation();
	const { goBack } = useHistory();
	const { id }: IdParamsType = useParams();
	const { state: { isFromRequiredFields } = { isFromRequiredFields: false } }: StateProps = useLocation();

	const formMethods = useForm({
		defaultValues,
		resolver: getFormDetails(event.mode),
	});

	// Holds the submitted form values while the notify-attendees confirm modal is open (date changed).
	const [pendingSave, setPendingSave] = useState<any>(null);
	const { notifyAsync } = useNotifyAttendeesOnDateChange();
	const [originalStartDate] = useState(event.days[0]?.start_date);
	const [notifyMessage, setNotifyMessage] = useState('');

	const changePassword = formMethods.watch('change_password');
	const startDate = formMethods.watch('dates.0.start_date');
	const timeZone = formMethods.watch('timezone');
	const hasPassword = event?.has_password;

	const datesHaveChanged = startDate && convertDateWithTimezoneToUTC(startDate, timeZone) !== originalStartDate;

	useEffect(() => {
		if (hasPassword && !changePassword) {
			formMethods.setValue('remove_password', true);
		} else {
			formMethods.setValue('remove_password', false);
		}
	}, [changePassword, hasPassword, formMethods]);

	useEffect(() => {
		if (!hasPassword && changePassword) {
			formMethods.setValue('want_to_change_password', true);
		} else if (!hasPassword && !changePassword) {
			formMethods.setValue('want_to_change_password', false);
		}
	}, [changePassword, hasPassword, formMethods]);

	const saveEvent = (values: any, notify: boolean, customMessage = '') => {
		editEvent(
			{ values, id },
			notify
				? {
						// Notify runs only after a successful save. The dark standalone view has no toast
						// system, so this is silent on success and the error is swallowed (do not extend the
						// red zone) — the show is already saved regardless.
						onSuccess: () => {
							notifyAsync({ eventIds: [id], customMessage }).catch(() => undefined);
						},
				  }
				: undefined
		);
	};

	const onEditEvent = (values: any) => {
		// Changing the show date affects everyone holding a ticket — offer to email them. Otherwise save directly.
		if (datesHaveChanged) {
			setPendingSave(values);
			return;
		}
		saveEvent(values, false);
	};

	useMemo(() => {
		if (event) {
			const allValues = handleInitialValues(event);
			formMethods.reset(allValues);
		}
	}, [event]);

	return (
		<Form formMethods={formMethods} onSubmit={onEditEvent}>
			<MainLayout>
				<EventTitle title={event?.name} />
				<ContentWrapper>
					<LeftContainer>
						<LeftForm isFromRequiredFields={isFromRequiredFields} hasPassword={hasPassword} />
					</LeftContainer>
					<RightContainer>
						<RightForm />
					</RightContainer>
				</ContentWrapper>
				<Footer>
					<Footer.Button type="button" onClick={goBack} category={OUTLINED} text={t('editEvent-cancel')} />
					<Footer.Button text={t('editEvent-save')} type="submit" />
				</Footer>

				{pendingSave && (
					<Modal onClose={() => setPendingSave(null)} isWide>
						<>
							<Modal.Header>{t('editEvent-notify-title')}</Modal.Header>
							<Modal.Body>
								<NotifyModalBody>
									<p>{t('editEvent-notify-text')}</p>
									<label htmlFor="editEvent-notify-message">{t('editEvent-notify-message-label')}</label>
									<textarea
										id="editEvent-notify-message"
										value={notifyMessage}
										onChange={(e) => setNotifyMessage(e.target.value)}
										placeholder={t('editEvent-notify-message-placeholder')}
										rows={3}
									/>
									<span className="previewLabel">{t('editEvent-notify-preview-label')}</span>
									<DateChangeEmailPreview
										data={{
											...buildDateChangePreview({
												eventName: pendingSave.name,
												eventId: id,
												startDate: pendingSave.dates?.[0]?.start_date,
												endDate: pendingSave.dates?.[0]?.end_date,
												timeZone: pendingSave.timezone,
												venueName: pendingSave.venue,
												eventBanner: event.event_webpage_settings?.event_banner,
												primaryColor: event.event_webpage_settings?.theme_colors,
											}),
											customMessage: notifyMessage.trim(),
										}}
									/>
								</NotifyModalBody>
							</Modal.Body>
							<Modal.ActionButtons>
								<Button
									category={QUATERNARY}
									type="button"
									text={t('editEvent-notify-skip')}
									onClick={() => {
										const values = pendingSave;
										setPendingSave(null);
										saveEvent(values, false);
									}}
								/>
								<Button
									category={TERTIARY}
									type="button"
									text={t('editEvent-notify-confirm')}
									onClick={() => {
										const values = pendingSave;
										setPendingSave(null);
										saveEvent(values, true, notifyMessage.trim());
									}}
								/>
							</Modal.ActionButtons>
						</>
					</Modal>
				)}
			</MainLayout>
		</Form>
	);
};
